import { Injectable } from '@angular/core';
import * as braze from '@braze/web-sdk';
import { environment } from 'src/environments/environment';
import {
  EngagementEventName,
  EngagementEventProperties,
  EngagementUserAttributes,
  EngagementUserId,
} from './engagement.types';

/**
 * Capa única de comunicación con la plataforma de engagement (hoy Braze).
 *
 * Reglas:
 *  - Las pantallas/flows NUNCA importan `@braze/web-sdk` directamente: usan este servicio.
 *  - Si Braze se reemplaza por otro vendor, sólo cambia esta implementación.
 *  - Todo lo de Braze cae en un no-op silencioso si `environment.braze.enabled === false`,
 *    para que no rompa builds donde todavía no se aprovisionó la API key.
 */
@Injectable({ providedIn: 'root' })
export class EngagementService {
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    const cfg = environment.braze;
    if (!cfg?.enabled || !cfg.apiKey || !cfg.baseUrl) return;
    braze.initialize(cfg.apiKey, {
      baseUrl: cfg.baseUrl,
      enableLogging: cfg.enableLogging ?? false,
      allowUserSuppliedJavascript: false,
    });
    braze.automaticallyShowInAppMessages();
    braze.openSession();
    this.initialized = true;
  }

  identifyUser(externalId: EngagementUserId, attributes?: EngagementUserAttributes): void {
    if (!this.initialized) return;
    braze.changeUser(externalId);
    if (!attributes) return;
    const user = braze.getUser();
    if (!user) return;
    if (attributes.email) user.setEmail(attributes.email);
    if (attributes.firstName) user.setFirstName(attributes.firstName);
    if (attributes.lastName) user.setLastName(attributes.lastName);
    if (attributes.phone) user.setPhoneNumber(attributes.phone);
    if (attributes.dob) {
      const date = new Date(attributes.dob);
      if (!Number.isNaN(date.getTime())) {
        user.setDateOfBirth(date.getFullYear(), date.getMonth() + 1, date.getDate());
      }
    }
  }

  trackEvent(name: EngagementEventName, properties?: EngagementEventProperties): void {
    if (!this.initialized) return;
    braze.logCustomEvent(name, properties as Record<string, unknown> | undefined);
  }

  setUserAttribute(key: string, value: string | number | boolean): void {
    if (!this.initialized) return;
    const user = braze.getUser();
    user?.setCustomUserAttribute(key, value);
  }
}
